import {
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { RoleOptionUi } from "../../types/documentManagementUiTypes";
import { parseRoleAccess } from "../../utils/documentManagementUiUtils";

const ALL_ROLES_VALUE = "*";

type Props = {
  roleAccessRaw: string;
  roleOptions: RoleOptionUi[];
  disabled?: boolean;
  onChange: (roleAccess: string[]) => void;
  /** Match user-management forms (`NewUser`) when `"standard"`. */
  variant?: "standard" | "outlined";
};

export function DocumentRoleAccessSelect({
  roleAccessRaw,
  roleOptions,
  disabled,
  onChange,
  variant = "outlined",
}: Props) {
  const { t } = useTranslation();
  const parsed = parseRoleAccess(roleAccessRaw);
  const allRoleNames = useMemo(
    () => roleOptions.map((role) => role.name),
    [roleOptions]
  );
  const explicitRoles = parsed.filter((value) => value !== ALL_ROLES_VALUE);

  const isFullRoleSelection = (roles: string[]) =>
    allRoleNames.length > 0 &&
    allRoleNames.every((name) => roles.includes(name));

  const allRolesSelected =
    parsed.includes(ALL_ROLES_VALUE) || isFullRoleSelection(explicitRoles);

  const selectValue = allRolesSelected
    ? [ALL_ROLES_VALUE, ...allRoleNames]
    : explicitRoles;

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const rawNext = event.target.value;
    const next =
      typeof rawNext === "string" ? rawNext.split(",") : [...rawNext];
    const nextHasAll = next.includes(ALL_ROLES_VALUE);
    const nextRoles = next.filter((value) => value !== ALL_ROLES_VALUE);

    if (nextHasAll && !allRolesSelected) {
      onChange([ALL_ROLES_VALUE]);
      return;
    }

    if (allRolesSelected && !nextHasAll) {
      onChange([]);
      return;
    }

    if (allRolesSelected && nextHasAll && !isFullRoleSelection(nextRoles)) {
      onChange(nextRoles);
      return;
    }

    if (!nextHasAll && isFullRoleSelection(nextRoles)) {
      onChange([ALL_ROLES_VALUE]);
      return;
    }

    onChange(nextRoles);
  };

  const isStandard = variant === "standard";
  const allRolesLabel = t("opva.dmAllRoles", {
    defaultValue: "All roles (unrestricted)",
  });
  const selectRolesPlaceholder = t("opva.dmSelectRoles", {
    defaultValue: "Select roles",
  });
  const roleSelectClassName = isStandard
    ? "dm-role-select dm-role-select--standard"
    : "dm-role-select";

  return (
    <FormControl
      fullWidth
      variant={variant}
      size={isStandard ? "medium" : "small"}
      className={roleSelectClassName}
      disabled={disabled}
    >
      <Select
        multiple
        variant={variant}
        value={selectValue}
        onChange={handleChange}
        displayEmpty
        renderValue={(selectedValues) => {
          const vals = selectedValues as string[];
          if (!vals.length) {
            return (
              <span style={{ color: "rgba(0, 0, 0, 0.42)" }}>
                {selectRolesPlaceholder}
              </span>
            );
          }
          if (vals.includes(ALL_ROLES_VALUE) || isFullRoleSelection(vals)) {
            return allRolesLabel;
          }
          return vals.join(", ");
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
      >
        <MenuItem value={ALL_ROLES_VALUE}>
          <Checkbox checked={allRolesSelected} size="small" />
          <ListItemText primary={allRolesLabel} />
        </MenuItem>
        {roleOptions.map((role) => (
          <MenuItem key={role.id} value={role.name}>
            <Checkbox
              checked={
                allRolesSelected || explicitRoles.includes(role.name)
              }
              size="small"
            />
            <ListItemText primary={role.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
